import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import _, { isEqual } from "lodash";
import {
  getOrgStrength,
  addStrength,
  updateStrength,
  deleteStrength,
  uploadFileToAWS,
  getUploadingFileData
} from "../../../service";
import { PlusIcon, RemoveIcon } from "../../../asssets/icons";
import { ImageUploader } from "../../common";
import { Spinner } from "../../form";
import { TrashIcon } from "@heroicons/react/24/outline";

const AddOrgStrength = ({ org }) => {
  const [originalStrengths, setOriginalStrengths] = useState([]);
  const [strengthSection, setStrengthSection] = useState([
    {
      _id: "",
      name_fr: "",
      name_en: "",
      name_de: "",
      name_it: "",
      logo: "",
      logo_obj: null,
      description_fr: "",
      description_en: "",
      description_de: "",
      description_it: "",
      vip_id_fr: "",
      vip_id_en: "",
      vip_id_de: "",
      vip_id_it: "",
      alternate_vip_ids: {
        en: [],
        de: [],
        fr: [],
        it: []
      }
    }
  ]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [saving, setSaving] = useState(false);

  const { orgId } = useParams();

  const fetchStrengths = async () => {
    try {
      const { data } = await getOrgStrength(orgId);
      data.length > 0 ? setStrengthSection(data) : addStrengthSection();
      setOriginalStrengths(data);
    } catch (error) {
      setOriginalStrengths([]);
    }
  };

  useEffect(() => {
    fetchStrengths();
  }, [orgId, org]);

  const addStrengthSection = () => {
    setStrengthSection([
      ...strengthSection,
      {
        _id: "",
        name_fr: "",
        name_en: "",
        name_de: "",
        name_it: "",
        logo: "",
        logo_obj: null,
        description_fr: "",
        description_en: "",
        description_de: "",
        description_it: "",
        short_description_fr: "",
        short_description_en: "",
        short_description_de: "",
        short_description_it: "",
        vip_id_fr: "",
        vip_id_en: "",
        vip_id_de: "",
        vip_id_it: "",
        alternate_vip_ids: {
          en: [],
          de: [],
          fr: [],
          it: []
        }
      }
    ]);
  };

  const removeStrengthSection = async (index) => {
    const updatedSections = [...strengthSection];
    const removedSection = updatedSections[index];
    if (removedSection._id !== "") await deleteStrength(orgId, { _id: removedSection._id });
    updatedSections.splice(index, 1);
    setStrengthSection(updatedSections);
  };

  const handleChange = (index, path, value) => {
    const updatedSections = _.cloneDeep([...strengthSection]);
    updatedSections[index] = _.set(updatedSections[index], path, value);
    setStrengthSection(updatedSections);
  };

  const handleUploadChange = (strengthId, file, index, key_name, obj_name) => {
    if (file !== undefined && file !== null && file.name !== undefined && file.name !== null) {
      const filename = `strengths-images/${orgId}${
        process.env.REACT_APP_BASE_URL?.includes("dev") ? "/dev" : ""
      }/${strengthId}_${key_name}`;
      const updatedSections = [...strengthSection];
      updatedSections[index] = {
        ...updatedSections[index],
        [obj_name]: file,
        [key_name]: filename
      };

      setStrengthSection(updatedSections);
    }
  };

  const uploadImage = async (image, filename) => {
    if (image !== undefined) {
      const {
        data: { url }
      } = await getUploadingFileData({
        name: filename,
        type: image.type
      });
      await uploadFileToAWS(url, image, image.type, (progress) => {
        setUploadProgress({ ...uploadProgress, [filename]: progress?.progress });
      });
    }
  };

  const saveStrength = async () => {
    setSaving(true);
    for (const [index, item] of strengthSection.entries()) {
      if (item?.logo_obj || item?.logo_obj?.name !== undefined) {
        setSaving(`Uploading image #${index + 1}...`);
        await uploadImage(item.logo_obj, item.logo);
      }

      if (item._id !== "") {
        if (!isEqual(item, originalStrengths[index])) {
          setSaving(`Updating strength #${index + 1}...`);
          await updateStrength(orgId, item);
        }
      } else {
        setSaving(`Adding strength #${index + 1}...`);
        await addStrength(orgId, item);
      }
    }

    setSaving("Strengths saved successfully");
    fetchStrengths();
    setTimeout(() => {
      setSaving(false);
    }, 1000);
  };

  return (
    <>
      <div className="container mx-auto p-4">
        <div className="flex justify-end mt-4 fixed top-4 right-4 z-[999999]">
          <button
            onClick={saveStrength}
            type="button"
            disabled={saving}
            className="flex flex-col gap-2 disabled:opacity-60 select-none py-2 font-bold text-md border bg-[#515151] text-[#FFFFFF] px-8 rounded-3xl">
            <span className="flex items-center gap-2">
              Save Strengths {saving && <Spinner isSubmitting={saving} size={16} color="#FFFFFF" />}
            </span>
            {saving && (
              <span className="block">
                <span className="text-xs text-gray-300">
                  {typeof saving === "string" ? saving : "Saving..."}
                </span>
              </span>
            )}
          </button>
        </div>
      </div>
      <div className="space-y-8">
        {strengthSection.map((section, index) => {
          return (
            <div key={index} className="container mx-auto">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Strength #{index + 1}</h3>
                  {index > 0 && (
                    <button
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      onClick={() => removeStrengthSection(index)}>
                      <RemoveIcon />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-6">
                  {/* Logo Section */}
                  <div className="col-span-2">
                    <ImageUploader
                      label="Logo"
                      image={section.logo_obj || (section.logo ? section.logo : null)}
                      setImage={(image) => {
                        handleUploadChange(section._id, image, index, "logo", "logo_obj");
                      }}
                      uploadProgress={uploadProgress[section.logo] || 0}
                    />
                  </div>

                  {/* Language Fields Section */}
                  <div className="col-span-2">
                    <div className="grid grid-cols-1 gap-4">
                      {org.languages?.map((language, languageIndex) => (
                        <div key={languageIndex} className="space-y-4">
                          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide border-b pb-2">
                            {language}
                          </h4>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Strength Name
                            </label>
                            <input
                              type="text"
                              name={`name_${language}`}
                              value={section[`name_${language}`] || ""}
                              onChange={(e) =>
                                handleChange(index, `name_${language}`, e.target.value)
                              }
                              placeholder={`Enter name in ${language.toUpperCase()}`}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Description
                            </label>
                            <textarea
                              name={`description_${language}`}
                              value={section[`description_${language}`] || ""}
                              onChange={(e) =>
                                handleChange(index, `description_${language}`, e.target.value)
                              }
                              placeholder={`Enter description in ${language.toUpperCase()}`}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none resize-none transition-colors"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Short description
                            </label>
                            <textarea
                              name={`short_description_${language}`}
                              value={section[`short_description_${language}`] || ""}
                              onChange={(e) =>
                                handleChange(index, `short_description_${language}`, e.target.value)
                              }
                              placeholder={`Enter description in ${language.toUpperCase()}`}
                              rows={1}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none resize-none transition-colors"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              VIP ID {language.toUpperCase()}
                            </label>
                            <input
                              type="text"
                              name={`vip_id_${language}`}
                              value={section[`vip_id_${language}`] || ""}
                              onChange={(e) =>
                                handleChange(index, `vip_id_${language}`, Number(e.target.value))
                              }
                              placeholder={`Enter VIP ID in ${language.toUpperCase()}`}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                            />

                            <div className="flex flex-col gap-2 my-2">
                              <p className="block text-xs font-medium text-gray-600">
                                Alternate VIP IDs in {language.toUpperCase()}
                              </p>
                              <div className="flex flex-row flex-wrap gap-2">
                                {section?.alternate_vip_ids?.[language] &&
                                  section?.alternate_vip_ids?.[language]?.map(
                                    (aVipId, aVipIndex) => {
                                      return (
                                        <div
                                          key={`alternate_vip_id_${language}_${aVipIndex}`}
                                          className="flex gap-2 items-center border border-gray-300 rounded-md px-2 py-1">
                                          <input
                                            className="px-3 py-2 w-20 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                                            type="text"
                                            name={`alternate_vip_id_${language}`}
                                            value={aVipId}
                                            onChange={(e) =>
                                              handleChange(
                                                index,
                                                `alternate_vip_ids.${language}.[${aVipIndex}]`,
                                                Number(e.target.value)
                                              )
                                            }
                                          />
                                          <button
                                            onClick={() => {
                                              const updatedSection = [
                                                ...section?.alternate_vip_ids?.[language]
                                              ]?.filter((id) => Number(id) !== Number(aVipId));

                                              handleChange(
                                                index,
                                                `alternate_vip_ids.${language}`,
                                                updatedSection
                                              );
                                            }}>
                                            <TrashIcon className="w-4 h-4 text-red-500" />
                                          </button>
                                        </div>
                                      );
                                    }
                                  )}
                                <button
                                  className="flex items-center gap-2 border border-gray-300 rounded-md px-2 py-1"
                                  onClick={() => {
                                    handleChange(
                                      index,
                                      `alternate_vip_ids.${language}.[${
                                        section?.alternate_vip_ids?.[language]?.length || 0
                                      }]`,
                                      ""
                                    );
                                  }}>
                                  <span className="w-4 h-4">
                                    <PlusIcon />
                                  </span>
                                  <span className="text-xs">Add</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-4 w-full">
        <div className="flex justify-end mt-8 w-full">
          <button
            className="flex items-center gap-2 px-6 py-3 bg-blue-200 text-[#606060] text-xl font-bold rounded-lg hover:bg-blue-400 transition-colors shadow-sm"
            onClick={() => addStrengthSection()}>
            <span className="w-8 h-8">
              <PlusIcon />
            </span>
            <span>Add New Strength</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default AddOrgStrength;
